import { Spinner } from "@blueprintjs/core";

import { Button } from "@/components/ui/button";
import { SharePermissionState } from "@/types/permissions";

import { ShareItem } from "./ShareItem";
import { ShareItemData } from "./types";

interface ShareListProps {
  items: ShareItemData[];
  onUpdateShareStatus?: (index: number, status: SharePermissionState) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  title?: string;
  showAllThreshold?: number;
  onToggleShowAll?: () => void;
  showingAll?: boolean;
}

export function ShareList({
  items,
  onUpdateShareStatus,
  loading = false,
  emptyMessage = "No shares yet",
  className,
  title = "People with access",
  showAllThreshold,
  onToggleShowAll,
  showingAll = false,
}: ShareListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner intent="primary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {emptyMessage}
      </div>
    );
  }

  const handleUpdateStatus =
    (index: number) => (status: SharePermissionState) => {
      onUpdateShareStatus?.(index, status);
    };

  const hasMore = showAllThreshold && items.length > showAllThreshold;
  const displayItems =
    hasMore && !showingAll ? items.slice(0, showAllThreshold) : items;

  return (
    <div className={className}>
      {title && <div className="font-semibold mb-1">{title}</div>}
      <div>
        {displayItems.map((item, index) => (
          <ShareItem
            key={`${item.type}-${
              item.type === "user"
                ? (item.item as any).email
                : (item.item as any).id
            }`}
            data={item}
            onUpdateShareStatus={
              onUpdateShareStatus ? handleUpdateStatus(index) : undefined
            }
          />
        ))}
      </div>
      {hasMore && onToggleShowAll && (
        <div className="flex justify-center w-full mt-2">
          <Button variant="minimal" size="sm" onClick={onToggleShowAll}>
            {showingAll ? "See Less" : "See More"}
          </Button>
        </div>
      )}
    </div>
  );
}
